import React, { useState, useEffect, useCallback } from 'react';
import './Articleship.css';
import micIcon from '../images/mic-icon.svg';

const determineBase = () => {
  if (process.env.REACT_APP_SERVER_URL) return process.env.REACT_APP_SERVER_URL;
  if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
    return 'http://localhost:8000';
  }
  return 'https://api-dev.chaimate.ai';
};

const API_BASE = determineBase();

const EXPERTISE_OPTIONS = [
  'Audit', 'Tax', 'Advisory', 'Risk & Forensics',
  'Corporate Finance', 'Transfer Pricing', 'Tax Litigation', 'Compliance'
];

const CONFETTI_COLORS = ['#34d399', '#f97316', '#f472b6', '#a855f7', '#6366f1'];

const PartnerDropdown = ({ partners, value, onChange, disabled }) => {
  const [open, setOpen] = useState(false);
  const displayText = value === '__others' 
    ? 'Other' 
    : value || (partners.length ? 'Select partner' : 'Select firm first');

  const handleSelect = (val) => {
    onChange(val);
    setOpen(false);
  };

  return (
    <div className="art-dropdown">
      <button
        type="button"
        className="art-dropdown-trigger"
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled}
      >
        <span>{displayText}</span>
        <span className="art-dropdown-arrow">▾</span>
      </button>
      {open && (
        <div className="art-dropdown-menu">
          <div className="art-dropdown-item" onClick={() => handleSelect('')}>
            Select partner
          </div>
          {partners.map((p, i) => (
            <div 
              key={i} 
              className={`art-dropdown-item ${value === p ? 'art-dropdown-selected' : ''}`}
              onClick={() => handleSelect(p)}
            >
              {p}
            </div>
          ))}
          <div 
            className={`art-dropdown-item ${value === '__others' ? 'art-dropdown-selected' : ''}`}
            onClick={() => handleSelect('__others')}
          >
            Other
          </div>
        </div>
      )}
    </div>
  );
};

const Articleship = () => {
  const [experiences, setExperiences] = useState([]);
  const [savedExperiences, setSavedExperiences] = useState([]);

  useEffect(() => {
    // Add first card on mount
    addExperience();
  }, []);

  const addExperience = () => {
    const newExp = {
      id: Date.now(),
      expanded: true,
      saved: false,
      phone: '',
      experienceType: experiences.length === 0 ? 'articleship' : 'articleship',
      city: '',
      company: '',
      otherCompany: '',
      manualCompanyName: '',
      manualCompanyAddress: '',
      isManualCompany: false,
      companySelectionMade: false,
      address: '',
      fromDate: '',
      toDate: '',
      partner: '',
      otherPartner: '',
      stipend: '',
      expertise: [],
      review: '',
      voiceBlob: null,
      voiceUrl: null,
      citySuggestions: [],
      companySuggestions: [],
      partners: [],
    };
    // Collapse all existing experiences when adding a new one
    setExperiences(prev => [
      ...prev.map(exp => ({ ...exp, expanded: false })),
      newExp
    ]);
  };

  const updateExperience = (id, updates) => {
    setExperiences(prev => prev.map(exp => 
      exp.id === id ? { ...exp, ...updates } : exp
    ));
  };

  const removeExperience = (id) => {
    setExperiences(prev => prev.filter(exp => exp.id !== id));
  };

  const fetchSuggestions = async (field, value) => {
    if (!value || value.length < 2) return [];
    try {
      const url = `${API_BASE}/icai/search?field=${field}&value=${encodeURIComponent(value)}&limit=15`;
      console.log('Fetching suggestions from:', url);
      const res = await fetch(url);
      if (!res.ok) {
        console.error('Search API error:', res.status, res.statusText);
        return [];
      }
      const data = await res.json();
      console.log('Search API response:', data);
      const result = Array.isArray(data) ? data : data?.data || [];
      console.log('Parsed suggestions:', result);
      return result;
    } catch (e) {
      console.error('Search error:', e);
      return [];
    }
  };

  const handleCitySearch = async (id, value) => {
    updateExperience(id, { city: value, citySuggestions: [] });
    if (value.length >= 2) {
      const suggestions = await fetchSuggestions('city', value);
      // Dedupe by city name
      const seen = new Set();
      const uniqueSuggestions = suggestions.filter(s => {
        if (!s.city || seen.has(s.city.toLowerCase())) return false;
        seen.add(s.city.toLowerCase());
        return true;
      });
      // Update suggestions only if the city value hasn't changed
      setExperiences(prev => prev.map(exp => {
        if (exp.id !== id) return exp;
        // Only update suggestions if the city input still matches what we searched for
        if (exp.city === value) {
          return { ...exp, citySuggestions: uniqueSuggestions };
        }
        return exp;
      }));
    }
  };

  const handleCompanySearch = async (id, value, selectedCity) => {
    updateExperience(id, { 
      company: value,
      isManualCompany: false,
      manualCompanyName: '',
      manualCompanyAddress: '',
      companySelectionMade: false
    });
    if (value.length >= 2) {
      const suggestions = await fetchSuggestions('firm_name', value);
      const filtered = selectedCity 
        ? suggestions.filter(s => s.city?.toLowerCase().includes(selectedCity.toLowerCase()))
        : suggestions;
      updateExperience(id, { companySuggestions: filtered });
    } else {
      updateExperience(id, { companySuggestions: [] });
    }
  };

  const selectCity = (id, city) => {
    updateExperience(id, { 
      city, 
      citySuggestions: [],
      company: '',
      address: '',
      partner: '',
      partners: [],
      manualCompanyName: '',
      manualCompanyAddress: '',
      isManualCompany: false,
      otherCompany: '',
      companySelectionMade: false
    });
  };

  const selectCompany = (id, item) => {
    updateExperience(id, {
      company: item.firm_name || '',
      address: item.address || '',
      partners: item.partners || [],
      partner: '',
      companySuggestions: [],
      otherCompany: '',
      manualCompanyName: '',
      manualCompanyAddress: '',
      isManualCompany: false,
      companySelectionMade: true,
    });
  };

  const toggleExpertise = (id, option) => {
    setExperiences(prev => prev.map(exp => {
      if (exp.id !== id) return exp;
      const expertise = exp.expertise.includes(option)
        ? exp.expertise.filter(e => e !== option)
        : [...exp.expertise, option];
      return { ...exp, expertise };
    }));
  };

  const isCardValid = (exp) => {
    const phone = exp.phone.replace(/\D/g, '');
    if (phone.length !== 10) return false;
    if (!exp.city) return false;
    const companyValue = exp.company === '__others'
      ? exp.manualCompanyName
      : (exp.company || (exp.experienceType === 'job' ? exp.otherCompany : ''));
    if (!companyValue) return false;
    if (!exp.stipend) return false;
    if (exp.expertise.length === 0) return false;
    if (exp.experienceType === 'articleship' && (!exp.fromDate || !exp.toDate)) return false;
    return true;
  };

  const launchConfetti = () => {
    for (let i = 0; i < 30; i++) {
      const el = document.createElement('span');
      el.className = 'art-confetti';
      el.style.left = `${Math.random() * 100}%`;
      el.style.backgroundColor = CONFETTI_COLORS[i % CONFETTI_COLORS.length];
      el.style.setProperty('--x', `${(Math.random() - 0.5) * 200}px`);
      el.style.animationDelay = `${Math.random() * 0.3}s`;
      document.body.appendChild(el);
      el.addEventListener('animationend', () => el.remove());
    }
  };

  const saveCard = async (id) => {
    const exp = experiences.find(e => e.id === id);
    if (!exp || exp.saved || !isCardValid(exp)) return;

    const companyValue = exp.company === '__others'
      ? exp.manualCompanyName
      : (exp.company || exp.otherCompany);

    const addressValue = exp.isManualCompany ? exp.manualCompanyAddress : exp.address;

    const payload = {
      id: exp.id,
      phone: exp.phone,
      experienceType: exp.experienceType,
      city: exp.city,
      company: companyValue,
      address: addressValue,
      from_date: exp.fromDate,
      to_date: exp.toDate,
      partner: exp.partner === '__others' ? exp.otherPartner : exp.partner,
      stipend: exp.stipend,
      expertise: exp.expertise,
      review: exp.review,
    };

    try {
      console.log('Sending payload:', payload);
      const formData = new FormData();
      formData.append('payload', JSON.stringify(payload));
      if (exp.voiceBlob) {
        formData.append('voice_note', exp.voiceBlob, 'voice-note.webm');
      }
      await fetch(`${API_BASE}/ca-connect/experience`, {
        method: 'POST',
        body: formData
      });
    } catch (e) {
      console.error('Save error:', e);
    }

    launchConfetti();
    updateExperience(id, { saved: true, expanded: false });
    setSavedExperiences(prev => (
      prev.some(item => item.id === exp.id) ? prev : [...prev, payload]
    ));
  };

  return (
    <div className="art-container">
      <div className="art-content">
        <header className="art-header">
          <h1>CA Connect</h1>
          <p>Share your articleship experience to help fellow CAs</p>
        </header>

        <div className="art-cards">
          {experiences.map((exp, idx) => (
            <ExperienceCard
              key={exp.id}
              exp={exp}
              index={idx}
              isFirst={idx === 0}
              onUpdate={(updates) => updateExperience(exp.id, updates)}
              onRemove={() => removeExperience(exp.id)}
              onCitySearch={(val) => handleCitySearch(exp.id, val)}
              onCompanySearch={(val) => handleCompanySearch(exp.id, val, exp.city)}
              onSelectCity={(city) => selectCity(exp.id, city)}
              onSelectCompany={(item) => selectCompany(exp.id, item)}
              onToggleExpertise={(opt) => toggleExpertise(exp.id, opt)}
              onSave={() => saveCard(exp.id)}
              isValid={isCardValid(exp)}
            />
          ))}
        </div>

        <div className="art-actions">
          <button className="art-btn-secondary" onClick={addExperience}>
            + Add another experience
          </button>
        </div>

        {savedExperiences.length > 0 && (
          <div className="art-success">
            <p>🎉 You are now a part of CA Connect! We will get back to you in a day!</p>
            <p>
              Download CA Connect app to get updates and find openings in top firms.{' '}
              <a
                className="art-share-link"
                href="https://play.google.com/store/apps/details?id=com.parichay.ca"
                target="_blank"
                rel="noreferrer"
              >
                Download app
              </a>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const ExperienceCard = ({
  exp, index, isFirst, onUpdate, onRemove, onCitySearch, onCompanySearch,
  onSelectCity, onSelectCompany, onToggleExpertise, onSave, isValid
}) => {
  const [recording, setRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks = [];
      
      recorder.ondataavailable = (e) => chunks.push(e.data);
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        const url = URL.createObjectURL(blob);
        onUpdate({ voiceBlob: blob, voiceUrl: url });
        stream.getTracks().forEach(t => t.stop());
      };
      
      recorder.start();
      setMediaRecorder(recorder);
      setRecording(true);
    } catch (e) {
      console.error('Recording error:', e);
    }
  };

  const stopRecording = () => {
    if (mediaRecorder) {
      mediaRecorder.stop();
      setRecording(false);
    }
  };

  const removeVoice = () => {
    if (exp.voiceUrl) URL.revokeObjectURL(exp.voiceUrl);
    onUpdate({ voiceBlob: null, voiceUrl: null });
  };

  if (exp.saved) {
    return (
      <div className="art-card art-card-saved">
        <div className="art-card-header">
          <span className="art-card-num">Experience {index + 1}</span>
          <span className="art-card-badge">✓ Saved</span>
        </div>
        <p className="art-card-summary">{exp.company} • {exp.city}</p>
      </div>
    );
  }

  return (
    <div className={`art-card ${exp.expanded ? 'art-card-expanded' : ''}`}>
      <div className="art-card-header">
        <span className="art-card-num">Experience {index + 1}</span>
        <div className="art-card-actions">
          <button 
            className="art-btn-toggle"
            onClick={() => onUpdate({ expanded: !exp.expanded })}
          >
            {exp.expanded ? 'Collapse' : 'Expand'}
          </button>
          {!isFirst && (
            <button className="art-btn-delete" onClick={onRemove}>×</button>
          )}
        </div>
      </div>

      {exp.expanded && (
        <div className="art-card-body">
          <div className="art-field">
            <label>Phone Number <span className="art-required">*</span></label>
            <input
              type="tel"
              maxLength={10}
              placeholder="10-digit mobile"
              value={exp.phone}
              onChange={(e) => onUpdate({ phone: e.target.value.replace(/\D/g, '') })}
            />
          </div>

          <div className="art-field">
            <label>Experience Type</label>
            <select
              value={exp.experienceType}
              onChange={(e) => onUpdate({ experienceType: e.target.value })}
              disabled={isFirst}
            >
              <option value="articleship">Articleship</option>
              <option value="job">Job</option>
            </select>
          </div>

          <div className="art-field">
            <label>City</label>
            <div className="art-autocomplete">
              <input
                type="text"
                placeholder="Type city..."
                value={exp.city}
                onChange={(e) => onCitySearch(e.target.value)}
              />
              {exp.citySuggestions.length > 0 && (
                <div className="art-suggestions">
                  {exp.citySuggestions.map((s, i) => (
                    <div key={i} className="art-suggestion" onClick={() => onSelectCity(s.city)}>
                      {s.city}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="art-field">
            <label>Company / Firm</label>
            <div className="art-autocomplete">
              <input
                type="text"
                placeholder={exp.company === '__others' ? 'Manual entry selected' : 'Search firm...'}
                value={exp.company === '__others' ? '' : exp.company}
                onChange={(e) => onCompanySearch(e.target.value)}
                disabled={exp.company === '__others'}
              />
              {exp.companySuggestions.length > 0 && exp.experienceType !== 'job' && (
                <div className="art-suggestions">
                  {exp.companySuggestions.map((s, i) => (
                    <div key={i} className="art-suggestion" onClick={() => onSelectCompany(s)}>
                      <strong>{s.firm_name}</strong>
                      <small>{s.city} • {s.partners?.length || 0} partners</small>
                    </div>
                  ))}
                </div>
              )}
              {exp.companySuggestions.length === 0 &&
                exp.experienceType !== 'job' &&
                exp.company.length >= 2 &&
                !exp.isManualCompany &&
                exp.company !== '__others' &&
                !exp.companySelectionMade && (
                <div className="art-suggestions">
                  <div
                    className="art-suggestion art-suggestion-other"
                    onClick={() => onUpdate({
                      company: '__others',
                      manualCompanyName: '',
                      manualCompanyAddress: '',
                      isManualCompany: true,
                      otherCompany: '',
                      companySuggestions: [],
                      address: '',
                      partners: [],
                      companySelectionMade: false
                    })}
                  >
                    Can't find the firm? Add it manually
                  </div>
                </div>
              )}
            </div>
            {exp.company === '__others' && exp.experienceType !== 'job' && (
              <div className="art-manual-entry">
                <input
                  type="text"
                  placeholder="Firm name"
                  value={exp.manualCompanyName}
                  onChange={(e) => onUpdate({ manualCompanyName: e.target.value })}
                />
                <textarea
                  placeholder="Firm address"
                  value={exp.manualCompanyAddress}
                  onChange={(e) => onUpdate({ manualCompanyAddress: e.target.value })}
                />
              </div>
            )}
          </div>

          {exp.experienceType === 'job' && (
            <div className="art-field">
              <label>Other Company Name</label>
              <input
                type="text"
                placeholder="If not in list..."
                value={exp.otherCompany}
                onChange={(e) => onUpdate({ otherCompany: e.target.value })}
              />
            </div>
          )}

          {exp.address && (
            <div className="art-field">
              <label>Address</label>
              <input type="text" value={exp.address} readOnly className="art-readonly" />
            </div>
          )}

          <div className="art-field">
            <label>Reporting Partner</label>
            <PartnerDropdown
              partners={exp.partners}
              value={exp.partner}
              onChange={(val) => onUpdate({ partner: val })}
              disabled={exp.partners.length === 0 && !exp.isManualCompany}
            />
            {exp.partner === '__others' && (
              <input
                type="text"
                placeholder="Enter partner name"
                value={exp.otherPartner}
                onChange={(e) => onUpdate({ otherPartner: e.target.value })}
                style={{ marginTop: '0.5rem' }}
              />
            )}
          </div>

          <div className="art-field">
            <label>Monthly Stipend (₹)</label>
            <input
              type="number"
              placeholder="e.g. 15000"
              value={exp.stipend}
              onChange={(e) => onUpdate({ stipend: e.target.value })}
            />
          </div>

          {exp.experienceType === 'articleship' && (
            <div className="art-field">
              <label>Articleship Duration</label>
              <div className="art-date-row">
                <div className="art-date-field">
                  <span className="art-date-label">From</span>
                <input
                  type="month"
                  value={exp.fromDate}
                  onChange={(e) => onUpdate({ fromDate: e.target.value })}
                />
                </div>
                <div className="art-date-field">
                  <span className="art-date-label">To</span>
                <input
                  type="month"
                  value={exp.toDate}
                  onChange={(e) => onUpdate({ toDate: e.target.value })}
                />
                </div>
              </div>
            </div>
          )}

          <div className="art-field">
            <label>Firm Expertise</label>
            <div className="art-checkboxes">
              {EXPERTISE_OPTIONS.map(opt => (
                <label key={opt} className="art-checkbox">
                  <input
                    type="checkbox"
                    checked={exp.expertise.includes(opt)}
                    onChange={() => onToggleExpertise(opt)}
                  />
                  <span>{opt}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="art-field">
            <label>Share Your Experience</label>
            <div className="art-voice-row">
              <textarea
                rows={2}
                placeholder="Text or voice note..."
                value={exp.review}
                onChange={(e) => onUpdate({ review: e.target.value })}
              />
              <button
                className={`art-mic-btn ${recording ? 'art-recording' : ''}`}
                onClick={recording ? stopRecording : startRecording}
              >
                <img src={micIcon} alt="mic" />
              </button>
            </div>
            {exp.voiceUrl && (
              <div className="art-voice-preview">
                <audio controls src={exp.voiceUrl} />
                <button onClick={removeVoice}>×</button>
              </div>
            )}
          </div>

          <div className="art-card-footer">
            <button
              className="art-btn-save"
              disabled={!isValid}
              onClick={onSave}
            >
              Save Experience
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Articleship;
